import { memo } from 'react';
import PropTypes from 'prop-types';
import { noop, isNil, isFunction } from 'lodash';
import { useTranslation } from 'react-i18next';
// Import UI Components
import { Button, Tooltip } from '@geneui/components';
// Import Components
import AccessControl from 'components/common/AccessControl';
import ActionLink from 'components/common/ActionLink';
// Import Hooks
import useHasPermission from 'hooks/UseHasPermission';
// Import Constants
import { DefinitionTypes, l, TemplateTypesLabels, ActionsIcons } from 'constants/common';
// Import Enums
import { DeliveryMethodEnum, PermissionEnum, SubscriptionSectionEnum, TemplateTypeEnum } from 'enums';

const TemplateActionsButtons = ({
    data,
    testTemplateAction,
    unarchiveTemplateAction,
    archiveTemplateAction,
    deleteTemplateAction,
    getCloneTemplatePath,
    editTemplateAction,
    getEditTemplatePath,
    handleActionClick,
    infoTemplateAction,
    isInfoVisible,
    isDeleteVisible,
    isEditVisible,
    isCloneVisible,
    isTestSendVisible,
    isUnArchiveVisible,
    isArchiveVisible,
    getClonePredefinedPath,
    isViewMode,
    shouldConfirmToEdit,
}) => {
    const { IsUsed, DefinitionType, ArchivedDate, DeliveryMethod } = data;

    const { t } = useTranslation();

    const isPredefined = DefinitionType === DefinitionTypes.predefined;
    const isArchive = !isNil(ArchivedDate);
    const { hasSubscriptionSection } = useHasPermission();

    const isPredefinedAvailable =
        hasSubscriptionSection(SubscriptionSectionEnum.Predefined) &&
        [
            DeliveryMethodEnum.DynamicPopUp,
            DeliveryMethodEnum.Email,
            DeliveryMethodEnum.Internal,
            DeliveryMethodEnum.SMS,
            DeliveryMethodEnum.Telegram,
            DeliveryMethodEnum.PushNotification,
            DeliveryMethodEnum.Viber,
            DeliveryMethodEnum.WhatsAppProvider,
            DeliveryMethodEnum.Hoory,
        ].includes(DeliveryMethod) &&
        isFunction(getClonePredefinedPath);

    const isVisibleDelete = () => isDeleteVisible ?? (!isViewMode && !IsUsed && !isPredefined);
    const isVisibleArchive = () => isArchiveVisible ?? (!isViewMode && !isArchive && !isPredefined);
    const isVisibleEdit = () => isEditVisible ?? (!isViewMode && !isArchive && !isPredefined);
    const isVisibleUnArchive = () => isUnArchiveVisible ?? (!isViewMode && isArchive);
    const isVisibleClone = () => isCloneVisible ?? !isViewMode;
    const isVisibleTestSend = () =>
        isTestSendVisible ??
        (!isViewMode &&
            ![TemplateTypesLabels[TemplateTypeEnum.PopUp], TemplateTypesLabels[TemplateTypeEnum.CMSPopUp]].includes(
                data.DeliveryMethod,
            ));

    return (
        <>
            {isVisibleDelete() && (
                <AccessControl permissions={PermissionEnum.DeleteTemplate}>
                    <Tooltip text={t(l.DeleteTemplate)}>
                        <Button
                            icon={ActionsIcons.Delete}
                            appearance="minimal"
                            color="primary"
                            onClick={() => deleteTemplateAction(data)}
                        />
                    </Tooltip>
                </AccessControl>
            )}
            {isVisibleEdit() && (
                <AccessControl permissions={PermissionEnum.EditTemplate}>
                    <Tooltip text={t(l.EditTemplate)}>
                        <ActionLink
                            icon={ActionsIcons.Edit}
                            appearance="minimal"
                            color="primary"
                            onClick={() => editTemplateAction(data)}
                            withConfirm={IsUsed || shouldConfirmToEdit}
                            redirectPath={getEditTemplatePath(data)}
                        />
                    </Tooltip>
                </AccessControl>
            )}
            {isVisibleUnArchive() && (
                <Tooltip text={t(l.UnArchiveTemplate)}>
                    <Button
                        icon={ActionsIcons.UnArchive}
                        appearance="minimal"
                        color="primary"
                        onClick={() => unarchiveTemplateAction(data)}
                    />
                </Tooltip>
            )}
            {isVisibleArchive() && (
                <Tooltip text={t(l.ArchiveTemplate)}>
                    <Button
                        icon={ActionsIcons.Archive}
                        appearance="minimal"
                        color="primary"
                        onClick={() => archiveTemplateAction(data)}
                    />
                </Tooltip>
            )}
            {isVisibleTestSend() && (
                <Tooltip text={t(l.TestTemplate)}>
                    <Button
                        icon={ActionsIcons.MessageClosed}
                        appearance="minimal"
                        color="primary"
                        onClick={() => testTemplateAction(data)}
                    />
                </Tooltip>
            )}
            {isVisibleClone() && (
                <AccessControl permissions={PermissionEnum.CreateTemplate}>
                    <Tooltip text={t(l.CloneTemplate)}>
                        <ActionLink
                            icon={ActionsIcons.Clone}
                            appearance="minimal"
                            color="primary"
                            redirectPath={getCloneTemplatePath(data)}
                            onRedirect={handleActionClick}
                        />
                    </Tooltip>
                </AccessControl>
            )}

            {isVisibleClone() && isPredefinedAvailable && (
                <AccessControl permissions={[PermissionEnum.CRMAdmin, PermissionEnum.CRMManager]}>
                    <Tooltip text={t(l.CloneAsPredefined)}>
                        <ActionLink
                            icon={ActionsIcons.Predefined}
                            appearance="minimal"
                            color="primary"
                            redirectPath={getClonePredefinedPath(data, false)}
                            onRedirect={handleActionClick}
                        />
                    </Tooltip>
                </AccessControl>
            )}

            {isInfoVisible && (
                <Tooltip text={t(l.TemplateInfo)}>
                    <Button
                        icon={ActionsIcons.Stop}
                        appearance="minimal"
                        color="primary"
                        onClick={() => infoTemplateAction(data)}
                    />
                </Tooltip>
            )}
        </>
    );
};

TemplateActionsButtons.propTypes = {
    data: PropTypes.object.isRequired,
    testTemplateAction: PropTypes.func,
    unarchiveTemplateAction: PropTypes.func,
    archiveTemplateAction: PropTypes.func,
    deleteTemplateAction: PropTypes.func,
    getCloneTemplatePath: PropTypes.func,
    editTemplateAction: PropTypes.func,
    getEditTemplatePath: PropTypes.func,
    handleActionClick: PropTypes.func,
    infoTemplateAction: PropTypes.func,
    getClonePredefinedPath: PropTypes.func,
    isInfoVisible: PropTypes.bool,
    isViewMode: PropTypes.bool,
    isDeleteVisible: PropTypes.bool,
    isEditVisible: PropTypes.bool,
    isCloneVisible: PropTypes.bool,
    isTestSendVisible: PropTypes.bool,
    isUnArchiveVisible: PropTypes.bool,
    isArchiveVisible: PropTypes.bool,
    shouldConfirmToEdit: PropTypes.bool,
};

TemplateActionsButtons.defaultProps = {
    testTemplateAction: noop,
    unarchiveTemplateAction: noop,
    archiveTemplateAction: noop,
    deleteTemplateAction: noop,
    getCloneTemplatePath: noop,
    editTemplateAction: noop,
    getEditTemplatePath: noop,
    handleActionClick: noop,
    infoTemplateAction: noop,
    isInfoVisible: false,
    isViewMode: false,
    isDeleteVisible: null,
    isEditVisible: null,
    isCloneVisible: null,
    isTestSendVisible: null,
    isUnArchiveVisible: null,
    isArchiveVisible: null,
    shouldConfirmToEdit: null,
};

export default memo(TemplateActionsButtons);
